'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Entity, Feature, Address, Review } from '../types';
import { beginOwnershipOAuth, type OwnershipProvider } from '../utils/ownershipAuth';
import { ownershipApiUrl } from '../utils/ownershipApi';
import { resolveReviewerApiUrl } from '../utils/reviewerApi';

interface OwnerData {
  id: string;
  email: string;
  name: string;
  status: string;
  verification_status: string;
  shopify_customer_id?: string;
}

interface Claim {
  claim_id: string;
  entity_id: number;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  reviewed_at?: string;
  rejection_reason?: string;
  entity?: Entity;
}

interface DeltaReport {
  delta_report_id: string;
  entity_id: number;
  total_changes: number;
  high_priority_changes: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  entity?: Entity;
  deltas?: Array<{
    field: string;
    current_value: any;
    suggested_value: any;
    change_type: string;
    priority: string;
  }>;
}

interface OwnedEntity extends Entity {
  claimed_at: string;
  approved_at: string;
}

interface ManualAccessRequest {
  fullName: string;
  email: string;
  phone: string;
  campsiteName: string;
  notes: string;
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_cancelled: 'The sign-in flow was cancelled before completing. Please try again.',
  oauth_denied: 'The provider denied the authentication request. Please choose a different provider or try again.',
  oauth_failed: 'We could not complete the sign-in with that provider. Please try another option or request manual access.',
  invalid_return: 'The return link we received was invalid. You can continue to the owner portal directly instead.',
};

const getAuthErrorMessage = (raw: string | null): string | null => {
  if (!raw) return null;
  const normalised = raw.trim().toLowerCase();
  if (AUTH_ERROR_MESSAGES[normalised]) {
    return AUTH_ERROR_MESSAGES[normalised];
  }
  try {
    const decoded = decodeURIComponent(normalised);
    if (AUTH_ERROR_MESSAGES[decoded]) {
      return AUTH_ERROR_MESSAGES[decoded];
    }
    if (decoded && decoded !== normalised) {
      return decoded.replace(/[_+]/g, ' ');
    }
  } catch (_) {
    // ignore decode errors
  }
  return raw.replace(/[_+]/g, ' ');
};

const OwnerPortalPage: React.FC = () => {
  const autoLoginAttempted = useRef(false);
  const loginRedirected = useRef(false);
  const returnUrlRef = useRef<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [owner, setOwner] = useState<OwnerData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'sites' | 'claims' | 'reports' | 'reviews' | 'settings'>('overview');
  const [ownedSites, setOwnedSites] = useState<OwnedEntity[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [deltaReports, setDeltaReports] = useState<DeltaReport[]>([]);
  const [siteReviews, setSiteReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responseSubmitting, setResponseSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  const [isStartingLogin, setIsStartingLogin] = useState(false);
  const [activeProvider, setActiveProvider] = useState<OwnershipProvider | null>(null);
  const [pendingProvider, setPendingProvider] = useState<OwnershipProvider | null>(null);
  const [showManualRequest, setShowManualRequest] = useState(false);
  const [manualRequest, setManualRequest] = useState<ManualAccessRequest>({
    fullName: '',
    email: '',
    phone: '',
    campsiteName: '',
    notes: '',
  });
  const [manualRequestError, setManualRequestError] = useState<string | null>(null);
  const [manualRequestSent, setManualRequestSent] = useState(false);

  const ownerSupportEmail =
    process.env.NEXT_PUBLIC_OWNER_SUPPORT_EMAIL || 'owners@campingaustralia.com.au';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    const tokenParam = url.searchParams.get('token');
    const errorParam = url.searchParams.get('error');
    const returnParam = url.searchParams.get('return');
    const loginParam = url.searchParams.get('login');
    const providerParam = url.searchParams.get('provider') as OwnershipProvider | null;

    const storedReturn = sessionStorage.getItem('ownership_oauth_return');
    const resolvedReturn = returnParam || storedReturn || null;
    if (resolvedReturn) {
      returnUrlRef.current = resolvedReturn;
      setReturnUrl(resolvedReturn);
      sessionStorage.setItem('ownership_oauth_return', resolvedReturn);
    }

    if (tokenParam) {
      localStorage.setItem('ownership_token', tokenParam);
      sessionStorage.removeItem('ownership_oauth_provider');
      setLoginErrorMessage(null);
      setLoginNotice('Signed in successfully - loading your dashboard...');
      url.searchParams.delete('token');
      url.searchParams.delete('error');
      url.searchParams.delete('provider');
      url.searchParams.delete('login');
    } else if (errorParam) {
      setLoginErrorMessage(getAuthErrorMessage(errorParam));
    }

    if (loginParam === '1' && providerParam && !autoLoginAttempted.current) {
      setPendingProvider(providerParam);
    } else if (loginParam === '1') {
      setLoginNotice((prev) => prev || 'Choose a sign-in option below to continue.');
    }

    if (returnParam) {
      url.searchParams.delete('return');
    }

    const cleanedSearch = url.searchParams.toString();
    const cleanedUrl = cleanedSearch ? `${url.pathname}?${cleanedSearch}` : url.pathname;
    if (cleanedUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, '', cleanedUrl);
    }

    (async () => {
      await checkAuthentication();
    })();
  }, []);

  const checkAuthentication = async () => {
    try {
      const token = localStorage.getItem('ownership_token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(ownershipApiUrl('/auth/verify-token'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success && result.valid) {
        setIsAuthenticated(true);
        setOwner({
          id: result.payload.owner_id,
          email: result.payload.email,
          name: result.payload.name,
          status: result.payload.status,
          verification_status: result.payload.verification_status,
          shopify_customer_id: result.payload.shopify_customer_id
        });
        
        // Load owner data
        await loadOwnerData();
      } else {
        localStorage.removeItem('ownership_token');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      localStorage.removeItem('ownership_token');
    } finally {
      setLoading(false);
    }
  };

  const loadOwnerData = async () => {
    try {
      const token = localStorage.getItem('ownership_token');
      
      // Load owned sites, claims, and delta reports in parallel
      const [sitesResponse, claimsResponse, reportsResponse] = await Promise.all([
        fetch(ownershipApiUrl('/entities'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(ownershipApiUrl('/claims'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(ownershipApiUrl('/delta-reports'), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (sitesResponse.ok) {
        const sitesResult = await sitesResponse.json();
        setOwnedSites(sitesResult.entities || []);
      }

      if (claimsResponse.ok) {
        const claimsResult = await claimsResponse.json();
        setClaims(claimsResult.claims || []);
      }

      if (reportsResponse.ok) {
        const reportsResult = await reportsResponse.json();
        setDeltaReports(reportsResult.reports || []);
      }
    } catch (error) {
      console.error('Failed to load owner data:', error);
      setError('Failed to load your data. Please refresh the page.');
    }
  };

  const loginOptions = [
    {
      provider: 'google' as const,
      label: 'Continue with Google',
      classes: 'bg-green-600 hover:bg-green-700 text-white',
      icon: (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      ),
    },
    {
      provider: 'facebook' as const,
      label: 'Continue with Facebook',
      classes: 'bg-blue-600 hover:bg-blue-700 text-white',
      icon: (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22 12a10 10 0 10-11.54 9.87v-6.99H7.9V12h2.56V9.8c0-2.53 1.5-3.94 3.8-3.94 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.88h-2.34v6.99A10 10 0 0022 12z" />
        </svg>
      ),
    },
    {
      provider: 'shopify' as const,
      label: 'Continue with Shopify',
      classes: 'bg-[#95BF47] hover:bg-[#5E8E3E] text-white',
      icon: (
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21.5 4.24c-.05-.38-.36-.68-.74-.72-.54-.05-5.33-.39-5.33-.39s-.89-.84-1.31-1.2c-.42-.36-1.23-.31-1.23-.31l-.74 1.86s-.9.27-1.49.43c-.59.16-1.28.52-1.59.78-.31.27-.44.55-.44.55l-2.09.26s-.64.07-.74.74c-.1.67-1.53 12.3-1.53 12.3s-.12.82.72.92c.84.1 4.51.61 4.51.61l1.16 1.79s.28.44.86.44h7.4c.71 0 .86-.59.86-.59s1.88-13.78 1.94-14.16c.06-.38-.2-.71-.2-.71L21.5 4.24zM11.11 5.9l1.39-.18.57-1.43s.09-.27.31-.27c.22 0 .42.21.42.21l.99.93-1.65.21-1.41.53-.62-.01zm1.73 9.89c-.57 0-1.09-.15-1.54-.42l-.17.97h-.96l1.1-6.2c.44-.28.98-.44 1.6-.44 1.34 0 2.16.84 2.16 2.18 0 1.52-.99 2.91-2.19 2.91zm-.25-4.38c-.21 0-.4.04-.57.11l-.44 2.47c.17.1.37.16.59.16.67 0 1.24-.78 1.24-1.73-.01-.62-.31-1.01-.82-1.01z" />
        </svg>
      ),
    },
  ];

  const startProviderLogin = useCallback(
    async (provider: OwnershipProvider) => {
      if (isStartingLogin || typeof window === 'undefined') {
        return;
      }
      setLoginErrorMessage(null);
      setLoginNotice(null);
      setManualRequestError(null);
      setManualRequestSent(false);
      setIsStartingLogin(true);
      setActiveProvider(provider);
      autoLoginAttempted.current = true;

      try {
        const fallbackReturn = window.location.href;
        const desiredReturn = returnUrlRef.current || fallbackReturn;
        await beginOwnershipOAuth(provider, desiredReturn);
      } catch (authError) {
        const message =
          authError instanceof Error
            ? authError.message
            : 'We could not start the sign-in flow. Please try again.';
        setLoginErrorMessage(message);
        setIsStartingLogin(false);
        setActiveProvider(null);
      }
    },
    [isStartingLogin]
  );

  const handleManualRequestChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setManualRequest((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (manualRequestError) {
      setManualRequestError(null);
    }
    if (manualRequestSent) {
      setManualRequestSent(false);
    }
  };

  const submitManualRequest = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (typeof window === 'undefined') {
      return;
    }

    const fullName = manualRequest.fullName.trim();
    const email = manualRequest.email.trim();
    const phone = manualRequest.phone.trim();
    const campsiteName = manualRequest.campsiteName.trim();
    const notes = manualRequest.notes.trim();

    if (!fullName || !email) {
      setManualRequestError('Please include both your name and email so we can reach you.');
      return;
    }

    const targetReturn = returnUrlRef.current || window.location.href;
    const subject = encodeURIComponent('Campsite owner portal access request');
    const bodyLines = [
      `Name: ${fullName}`,
      `Email: ${email}`,
      phone ? `Phone: ${phone}` : null,
      campsiteName ? `Campsite: ${campsiteName}` : null,
      targetReturn ? `Reference link: ${targetReturn}` : null,
      '',
      notes ? `Notes: ${notes}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    const greeting = `Hi Camping Australia team,\n\nI'd like to administer a campsite listing on Camping Australia. My details are below:\n\n`;
    const closing = '\n\nThanks!\n';
    const body = encodeURIComponent(`${greeting}${bodyLines}${closing}`);

    window.location.href = `mailto:${ownerSupportEmail}?subject=${subject}&body=${body}`;
    setManualRequestSent(true);
    setManualRequestError(null);
  };

  useEffect(() => {
    if (!pendingProvider) {
      return;
    }

    startProviderLogin(pendingProvider)
      .catch(() => {
        // Errors are handled within startProviderLogin
      })
      .finally(() => {
        setPendingProvider(null);
      });
  }, [pendingProvider, startProviderLogin]);

  const handleLogout = () => {
    localStorage.removeItem('ownership_token');
    setIsAuthenticated(false);
    setOwner(null);
    setOwnedSites([]);
    setClaims([]);
    setDeltaReports([]);
    setLoginNotice(null);
    setLoginErrorMessage(null);
    setReturnUrl(null);
    returnUrlRef.current = null;
    loginRedirected.current = false;
  };

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') {
      return;
    }
    if (loginRedirected.current) {
      return;
    }

    const target = returnUrlRef.current;
    if (!target) {
      loginRedirected.current = true;
      sessionStorage.removeItem('ownership_oauth_return');
      return;
    }

    try {
      const parsed = new URL(target, window.location.origin);
      if (parsed.origin !== window.location.origin) {
        sessionStorage.removeItem('ownership_oauth_return');
        loginRedirected.current = true;
        return;
      }

      if (parsed.href !== window.location.href) {
        loginRedirected.current = true;
        sessionStorage.removeItem('ownership_oauth_return');
        setLoginNotice('Taking you back to where you started...');
        window.location.href = parsed.href;
      } else {
        loginRedirected.current = true;
        sessionStorage.removeItem('ownership_oauth_return');
      }
    } catch (redirectError) {
      console.error('Failed to process return URL:', redirectError);
      loginRedirected.current = true;
      sessionStorage.removeItem('ownership_oauth_return');
      setLoginErrorMessage(
        'You are signed in, but we could not return you to the previous page. Continue below.'
      );
    }
  }, [isAuthenticated]);

  const approveDeltaChanges = async (reportId: string, approvedChanges: string[]) => {
    try {
      const token = localStorage.getItem('ownership_token');
      
      const response = await fetch(ownershipApiUrl(`/delta-reports/${reportId}/approve`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ approved_changes: approvedChanges })
      });

      const result = await response.json();
      
      if (result.success) {
        // Refresh delta reports
        await loadOwnerData();
        alert('Changes applied successfully!');
      } else {
        alert(`Failed to approve changes: ${result.error}`);
      }
    } catch (error) {
      console.error('Error approving changes:', error);
      alert('An error occurred while approving changes.');
    }
  };

  const rejectDeltaReport = async (reportId: string, reason: string) => {
    try {
      const token = localStorage.getItem('ownership_token');
      
      const response = await fetch(ownershipApiUrl(`/delta-reports/${reportId}/reject`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      const result = await response.json();
      
      if (result.success) {
        // Refresh delta reports
        await loadOwnerData();
        alert('Delta report rejected.');
      } else {
        alert(`Failed to reject report: ${result.error}`);
      }
    } catch (error) {
      console.error('Error rejecting report:', error);
      alert('An error occurred while rejecting the report.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-white py-12 px-4">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center">
            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-green-700">
              Campsite Hosts
            </span>
            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl">
              Camping Australia Owner Portal
            </h1>
            <p className="mt-3 text-base text-gray-600 sm:text-lg">
              Sign in to manage your campsite listings, review claims, and keep your details up to date.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="rounded-2xl border border-green-100 bg-white p-8 shadow-sm">
              <div className="mb-6 flex items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">Sign in to continue</h2>
                  <p className="text-sm text-gray-500">Choose a provider you already use for your business.</p>
                </div>
              </div>

              {loginNotice && (
                <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  {loginNotice}
                </div>
              )}

              {loginErrorMessage && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {loginErrorMessage}
                </div>
              )}

              <div className="space-y-3">
                {loginOptions.map((option) => {
                  const isActive = activeProvider === option.provider && isStartingLogin;
                  const isDisabled = isStartingLogin && !isActive;

                  return (
                    <button
                      key={option.provider}
                      type="button"
                      onClick={() => startProviderLogin(option.provider)}
                      disabled={isDisabled}
                      className={`flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold transition ${
                        option.classes
                      } ${isDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      {isActive ? (
                        <>
                          <svg
                            className="mr-2 h-4 w-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Connecting...
                        </>
                      ) : (
                        <>
                          {option.icon}
                          {option.label}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="mt-5 text-left text-xs text-gray-500">
                We'll redirect you to your selected provider to verify your identity and bring you straight back.
              </p>

              {returnUrl && (
                <p className="mt-2 break-words text-left text-xs text-gray-500">
                  After signing in you'll return to:{' '}
                  <span className="font-medium text-gray-700">{returnUrl}</span>
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">Need manual approval instead?</h3>
              <p className="mt-2 text-sm text-gray-600">
                If you can't use one of the providers above, send our team a quick message and we'll enable access
                manually once we've verified your details.
              </p>

              <button
                type="button"
                onClick={() => setShowManualRequest((prev) => !prev)}
                className="mt-5 inline-flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                {showManualRequest ? 'Hide manual request form' : 'Open manual request form'}
              </button>

              {manualRequestSent && (
                <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  We've opened your email client with a draft message. Send it when you're ready and we'll get back to
                  you shortly.
                </div>
              )}

              {manualRequestError && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {manualRequestError}
                </div>
              )}

              {showManualRequest && (
                <form onSubmit={submitManualRequest} className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="fullName">
                      Your name *
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      value={manualRequest.fullName}
                      onChange={handleManualRequestChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      placeholder="Jane Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="email">
                      Email address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={manualRequest.email}
                      onChange={handleManualRequestChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="phone">
                      Phone (optional)
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      value={manualRequest.phone}
                      onChange={handleManualRequestChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      placeholder="+61 400 000 000"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="campsiteName">
                      Campsite or business name
                    </label>
                    <input
                      id="campsiteName"
                      name="campsiteName"
                      value={manualRequest.campsiteName}
                      onChange={handleManualRequestChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      placeholder="Misty Mountains Campground"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="notes">
                      Notes (optional)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      value={manualRequest.notes}
                      onChange={handleManualRequestChange}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      placeholder="Share anything that will help us confirm ownership or management."
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex w-full items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                  >
                    Prepare email request
                  </button>
                </form>
              )}

              <p className="mt-6 text-xs text-gray-500">
                You can also email us directly at <span className="font-medium text-gray-700">{ownerSupportEmail}</span>{' '}
                with any questions.
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500">
            By continuing you agree to our owner terms and the Camping Australia privacy policy.
          </p>
        </div>
      </div>
    );
  }

  // ── Reviews for owned sites ────────────────────────────────

  const loadSiteReviews = useCallback(async () => {
    if (ownedSites.length === 0) return;
    setReviewsLoading(true);
    try {
      const allReviews: Review[] = [];
      for (const site of ownedSites) {
        const res = await fetch(
          resolveReviewerApiUrl(`/entities/${site.id}/reviews?page=1&per_page=50&sort=recent`)
        );
        if (res.ok) {
          const data = await res.json();
          const reviews = (data.reviews || []).map((r: Review) => ({
            ...r,
            entity_id: site.id,
            _entityName: site.name,
          }));
          allReviews.push(...reviews);
        }
      }
      // Sort newest first
      allReviews.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setSiteReviews(allReviews);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  }, [ownedSites]);

  useEffect(() => {
    if (activeTab === 'reviews' && ownedSites.length > 0 && siteReviews.length === 0 && !reviewsLoading) {
      loadSiteReviews();
    }
  }, [activeTab, ownedSites, siteReviews.length, reviewsLoading, loadSiteReviews]);

  const submitOwnerResponse = async (reviewId: string) => {
    if (!responseText.trim()) return;
    setResponseSubmitting(true);
    try {
      const token = localStorage.getItem('ownership_token');
      const res = await fetch(resolveReviewerApiUrl(`/reviews/${reviewId}/owner-response`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ response_text: responseText.trim() }),
      });
      if (res.ok) {
        // Update local state with the new response
        setSiteReviews((prev) =>
          prev.map((r) =>
            r.id === reviewId
              ? { ...r, owner_response: { content: responseText.trim(), responded_at: new Date().toISOString() } }
              : r
          )
        );
        setRespondingTo(null);
        setResponseText('');
      } else {
        const err = await res.json().catch(() => ({ error: 'Failed' }));
        setError(err.error || 'Failed to submit response');
      }
    } catch (err) {
      console.error('Owner response failed:', err);
      setError('Failed to submit your response. Please try again.');
    } finally {
      setResponseSubmitting(false);
    }
  };

  const renderReviews = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-2">Reviews for Your Campsites</h2>
        <p className="text-gray-600 text-sm mb-6">
          View and respond to reviews left by campers on your sites. Your responses appear publicly beneath each review.
        </p>

        {reviewsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mr-3"></div>
            <span className="text-gray-600">Loading reviews...</span>
          </div>
        ) : siteReviews.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-gray-600">
              Once campers review your sites, they&apos;ll appear here for you to respond to.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {siteReviews.map((review) => (
              <div key={review.id} className="border rounded-lg p-5 hover:border-green-200 transition-colors">
                {/* Review header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="text-sm font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      {(review as any)._entityName || `Site #${review.entity_id}`}
                    </span>
                    <div className="flex items-center mt-2 space-x-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs">
                        {review.reviewer?.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{review.reviewer?.display_name || 'Anonymous'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(review.created_at).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {review.visit_date && (
                            <span className="ml-2">
                              &middot; Visited {review.visit_date}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className={`w-4 h-4 ${star <= review.overall_rating ? 'text-yellow-400' : 'text-gray-200'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>

                {/* Review content */}
                <h4 className="font-semibold text-gray-900 mb-1">{review.title}</h4>
                <p className="text-gray-700 text-sm leading-relaxed">{review.content}</p>

                {/* Images */}
                {review.images && review.images.length > 0 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto">
                    {review.images.map((img) => (
                      <img
                        key={img.id}
                        src={img.image_src}
                        alt="Review photo"
                        className="w-16 h-16 object-cover rounded border"
                      />
                    ))}
                  </div>
                )}

                {/* Owner response */}
                {review.owner_response ? (
                  <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4 rounded-r">
                    <div className="flex items-center mb-1">
                      <span className="text-xs font-semibold text-green-800 bg-green-200 px-2 py-0.5 rounded">Owner Response</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(review.owner_response.responded_at).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{review.owner_response.content}</p>
                  </div>
                ) : (
                  <div className="mt-4">
                    {respondingTo === review.id ? (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your response (visible publicly)
                        </label>
                        <textarea
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          maxLength={500}
                          rows={3}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Thank the camper and address their feedback..."
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{responseText.length}/500</span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setRespondingTo(null);
                                setResponseText('');
                              }}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => submitOwnerResponse(review.id)}
                              disabled={!responseText.trim() || responseSubmitting}
                              className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {responseSubmitting ? 'Sending...' : 'Post Response'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setRespondingTo(review.id);
                          setResponseText('');
                        }}
                        className="text-sm text-green-600 hover:text-green-800 font-medium"
                      >
                        Reply to this review
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Owned Sites</h3>
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <p className="text-3xl font-bold text-green-600">{ownedSites.length}</p>
        <p className="text-gray-600 text-sm">Active listings</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Pending Claims</h3>
          <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-3xl font-bold text-yellow-500">{claims.filter(c => c.status === 'pending').length}</p>
        <p className="text-gray-600 text-sm">Under review</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Pending Updates</h3>
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </div>
        <p className="text-3xl font-bold text-blue-600">{deltaReports.filter(r => r.status === 'pending').length}</p>
        <p className="text-gray-600 text-sm">Need review</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border cursor-pointer hover:border-purple-300 transition-colors" onClick={() => setActiveTab('reviews')}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Reviews</h3>
          <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </div>
        <p className="text-3xl font-bold text-purple-600">{siteReviews.length}</p>
        <p className="text-gray-600 text-sm">
          {siteReviews.filter(r => !r.owner_response).length} need a response
        </p>
      </div>
    </div>
  );

  const renderOwnedSites = () => (
    <div className="space-y-6">
      {ownedSites.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No owned sites yet</h3>
          <p className="text-gray-600">Start by claiming a campsite listing to manage it here.</p>
        </div>
      ) : (
        ownedSites.map((site) => (
          <div key={site.id} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{site.name}</h3>
                <p className="text-gray-600">{site.addresses?.[0]?.town}, {site.addresses?.[0]?.state}</p>
                <p className="text-sm text-gray-500">
                  Claimed: {new Date(site.claimed_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex space-x-2">
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
                  Edit
                </button>
                <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm">
                  View Public
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Description</p>
                <p className="text-gray-900">{site.short_desc || 'No description available'}</p>
              </div>
              <div>
                <p className="text-gray-500">Features</p>
                <p className="text-gray-900">{site.features?.length || 0} features listed</p>
              </div>
              <div>
                <p className="text-gray-500">Images</p>
                <p className="text-gray-900">{site.gallery_images?.length || 0} images</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderClaims = () => (
    <div className="space-y-6">
      {claims.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No claims submitted</h3>
          <p className="text-gray-600">Visit campsite pages to submit ownership claims.</p>
        </div>
      ) : (
        claims.map((claim) => (
          <div key={claim.claim_id} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {claim.entity?.name || `Entity #${claim.entity_id}`}
                </h3>
                <p className="text-sm text-gray-600">Claim ID: {claim.claim_id}</p>
                <p className="text-sm text-gray-500">
                  Submitted: {new Date(claim.submitted_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                claim.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                claim.status === 'approved' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
              </span>
            </div>
            
            {claim.status === 'rejected' && claim.rejection_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-red-900 mb-1">Rejection Reason:</h4>
                <p className="text-red-700 text-sm">{claim.rejection_reason}</p>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderDeltaReports = () => (
    <div className="space-y-6">
      {deltaReports.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No update reports available</h3>
          <p className="text-gray-600">We'll notify you when we detect potential updates for your listings.</p>
        </div>
      ) : (
        deltaReports.map((report) => (
          <div key={report.delta_report_id} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {report.entity?.name || `Entity #${report.entity_id}`}
                </h3>
                <p className="text-sm text-gray-600">
                  {report.total_changes} potential updates found
                  {report.high_priority_changes > 0 && (
                    <span className="text-red-600 ml-2">
                      ({report.high_priority_changes} high priority)
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  Generated: {new Date(report.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                report.status === 'approved' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
              </span>
            </div>
            
            {report.status === 'pending' && (
              <div className="flex space-x-3 mt-4">
                <button
                  onClick={() => approveDeltaChanges(report.delta_report_id, report.deltas?.map(d => d.field) || [])}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
                >
                  Approve All Changes
                </button>
                <button
                  onClick={() => rejectDeltaReport(report.delta_report_id, 'Not interested in these changes')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Reject Report
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Owner Portal</h1>
              <p className="text-gray-600">Welcome back, {owner?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`px-3 py-1 rounded-full text-sm ${
                owner?.verification_status === 'verified' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {owner?.verification_status === 'verified' ? 'Verified Owner' : 'Pending Verification'}
              </div>
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'sites', label: 'My Sites' },
              { key: 'claims', label: 'Claims' },
              { key: 'reports', label: 'Update Reports' },
              { key: 'reviews', label: 'Reviews' },
              { key: 'settings', label: 'Settings' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'sites' && renderOwnedSites()}
        {activeTab === 'claims' && renderClaims()}
        {activeTab === 'reports' && renderDeltaReports()}
        {activeTab === 'reviews' && renderReviews()}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-4">Settings</h2>
            <p className="text-gray-600">Settings panel coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OwnerPortalPage;

